// How a project row reads: its derived status and the four formatters the
// list and its neighbours share. Kept beside the page so that file exports
// a component and nothing else.

import type { ProjectListItem } from "../auth/useAuth";

/** "setup" is a UI-only state: an active project with nothing on it yet. */
export type ProjectStatus = "active" | "setup" | "suspended";

/**
 * Derive the visual status from a project's data. "suspended" is the stored
 * server status; "setup" is a UI-only derived state (active + no activity yet);
 * everything else is "active".
 */
export function deriveStatus(p: ProjectListItem): ProjectStatus {
  if (p.status === "suspended") return "suspended";
  if (!p.last_activity_at) return "setup";
  return "active";
}

/** Short relative-time formatter — "just now", "2h ago", "3d ago", "1w ago", "Apr 22". */
export function humanizeRelativeDate(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = now.getTime() - t;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 4) return `${wk}w ago`;
  // Older than ~a month — fall back to absolute month + day, no year.
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

/** Long absolute date — "Mar 14, 2024". */
export function formatCreatedDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

/** Grouped player count — "12,847". Zero renders as "0", never a dash. */
export function formatPlayers(count: number): string {
  return count.toLocaleString("en-US");
}

/** "e74c…a82f" — first 4 chars of UUID + ellipsis + last 4. */
export function truncateId(id: string): string {
  const clean = id.replace(/-/g, "");
  if (clean.length < 9) return id;
  return `${clean.slice(0, 4)}…${clean.slice(-4)}`;
}
